/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address';
import { IAddressSelectItem, IAddressSelectProps } from '@msdyn365-commerce-modules/address';
import { IAddressShowItem, IAddressShowProps } from '@msdyn365-commerce-modules/address';
import { ICheckoutShippingAddressViewProps } from '@msdyn365-commerce-modules/address';
import { Address, AttributeTextValue } from '@msdyn365-commerce/retail-proxy';
import { set, get } from 'mobx';
import { ICheckoutState } from '@msdyn365-commerce/global-state';

/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @returns - Address Node.
 */
const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items }) => {
    return (
        <Node {...AddressDetail}>
            {items.map((item: IAddressShowItem) => {
                return <>{item.description}</>;
            })}
        </Node>
    );
};

/**
 * Address Select Component.
 * @param param0 - Root param.
 * @param param0.SelectAddress - Select address.
 * @param param0.addButton - Add button.
 * @param param0.items - IAddressSelectItem[].
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns - SelectAddress Node.
 */
const AddressSelect: React.FC<IAddressSelectProps> = ({
    SelectAddress,
    addButton,
    items,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {
    return (
        <Node {...SelectAddress}>
            {addButton}
            {items.map((item: IAddressSelectItem) => {
                const SelectItem = item.SelectItem;
                return (
                    <Node {...SelectItem} key={item.key}>
                        {item.input}
                        <AddressShow {...item.showItems} />
                    </Node>
                );
            })}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Address Add Update Component.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - Error message.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns Address add update component node.
 */
const AddressAddUpdate: React.FC<IAddressAddUpdateProps & {
    addUpdateAddress: Address;
    validationError: object;
    checkout?: ICheckoutState;
}> = ({
    addUpdateAddress,
    validationError,
    checkout,
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {
    const [firstName, setFirstName] = React.useState('');
    const [firstNameTouched, setFirstNameTouched] = React.useState(false);
    const [lastName, setLastName] = React.useState('');
    const [lastNameTouched, setLastNameTouched] = React.useState(false);

    React.useEffect(() => {
        const fullName = firstName && lastName ? `${firstName} ${lastName}` : '';
        set(addUpdateAddress, { Name: fullName });

        if (checkout && firstName && lastName) {
            const attrs = checkout.attributeValues?.slice() ?? [];

            let heiAttr = attrs.find(a => a.Name === 'DeliveryName') as AttributeTextValue | undefined;

            const attrVal = JSON.stringify({ firstName, lastName });
            if (heiAttr?.TextValue !== attrVal) {
                if (!heiAttr) {
                    heiAttr = {
                        // @ts-ignore
                        '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                        Name: 'DeliveryName',
                        ExtensionProperties: []
                    };

                    attrs.push(heiAttr!);
                }

                heiAttr!.TextValue = attrVal;
                process.env.NODE_ENV === 'development' && console.log('checkout-shipping-address', 'DeliveryName', attrs);
                void checkout
                    .updateAttributeValues({ newAttributeValues: attrs })
                    .then(() => checkout.checkoutCart.updateAttributeValues({ newAttributeValues: attrs }));
            }
        }
    }, [checkout, addUpdateAddress, firstName, lastName]);

    React.useEffect(() => {
        if (checkout) {
            const attrs = checkout.attributeValues ?? [];
            process.env.NODE_ENV === 'development' &&
                console.log(
                    'checkout-shipping-address',
                    JSON.stringify({
                        av1: checkout.attributeValues,
                        av2: checkout.checkoutCart.attributeValues,
                        av4: checkout.checkoutCart.cart.AttributeValues,
                        av3: checkout.salesOrder?.AttributeValues ?? []
                    })
                );

            let heiAttr = attrs.find(a => a.Name === 'DeliveryName') as AttributeTextValue | undefined;
            if (!heiAttr) {
                heiAttr = checkout.checkoutCart.cart.AttributeValues?.find(a => a.Name === 'DeliveryName');
            }

            if (heiAttr?.TextValue) {
                try {
                    const name = JSON.parse(heiAttr.TextValue) as { firstName: string; lastName: string };
                    setFirstName(name?.firstName ?? '');
                    setLastName(name?.lastName ?? '');
                } catch (err) {
                    process.env.NODE_ENV === 'development' && console.warn('Fail to parse address name', err);
                }
            }
        }
    }, [checkout]);

    return (
        <Node {...AddressForm}>
            {heading}
            {items.map((item: IAddressAddItem) => {
                const { AddressItem, key, label, alert, input } = item;

                process.env.NODE_ENV === 'development' && console.log('AddressAddUpdate', key, AddressItem);

                if (key === 'ThreeLetterISORegionName') {
                    // default country to Canada
                    if (addUpdateAddress.ThreeLetterISORegionName !== 'CAN') {
                        set(addUpdateAddress, { ThreeLetterISORegionName: 'CAN' });
                    }

                    return (
                        <Node {...AddressItem} key={key}>
                            {label}
                            {alert}
                            <select disabled className='msc-address-form__dropdown' name='ThreeLetterISORegionName' aria-required='true'>
                                <option value='CAN' aria-selected='true'>
                                    Canada
                                </option>
                            </select>
                        </Node>
                    );
                } else if (key === 'Name') {
                    const errorMessage = get(validationError, 'Name');
                    return (
                        <React.Fragment key={key}>
                            <Node {...AddressItem}>
                                <label className='msc-address-form__label' htmlFor='hei_shipping_address_firstname'>
                                    First Name *
                                </label>
                                {(!!errorMessage || firstNameTouched) && !firstName && (
                                    <span className='msc-address-form__alert' role='alert' aria-live='assertive'>
                                        First Name is required
                                    </span>
                                )}
                                <span className='d-none'>{input}</span>
                                <input
                                    id='hei_shipping_address_firstname'
                                    name='FirstName'
                                    className='msc-address-form__input msc-address-form__input-text'
                                    type='text'
                                    maxLength={50}
                                    aria-required='true'
                                    value={firstName}
                                    onChange={evt => {
                                        setFirstName(evt.target.value);
                                        setFirstNameTouched(true);
                                    }}
                                ></input>
                            </Node>
                            <Node {...AddressItem} key={key}>
                                <label className='msc-address-form__label' htmlFor='hei_shipping_address_lastname'>
                                    Last Name *
                                </label>
                                {(!!errorMessage || lastNameTouched) && !lastName && (
                                    <span className='msc-address-form__alert' role='alert' aria-live='assertive'>
                                        Last Name is required
                                    </span>
                                )}
                                <span className='d-none'>{input}</span>
                                <input
                                    id='hei_shipping_address_lastname'
                                    name='LastName'
                                    className='msc-address-form__input msc-address-form__input-text'
                                    type='text'
                                    maxLength={50}
                                    aria-required='true'
                                    value={lastName}
                                    onChange={evt => {
                                        setLastName(evt.target.value);
                                        setLastNameTouched(true);
                                    }}
                                ></input>
                            </Node>
                        </React.Fragment>
                    );
                }

                return (
                    <Node {...AddressItem} key={key}>
                        {label}
                        {alert}
                        {input}
                    </Node>
                );
            })}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Checkout Shipping Address View Component.
 * @param props - Props.
 * @returns - CheckoutShippingAddress Module.
 */
const CheckoutShippingAddressView: React.FC<ICheckoutShippingAddressViewProps> = props => {
    const {
        addUpdateAddress,
        validationError,
        CheckoutShippingAddress,
        checkoutErrorRef,
        alert,
        viewState,
        showAddress,
        showAddressSelect,
        showAddOrUpdateAddress,
        cartLineImages
    } = props;

    return (
        <Module {...CheckoutShippingAddress} ref={checkoutErrorRef}>
            {alert}
            {cartLineImages}
            {viewState.isShowAddress && <AddressShow {...showAddress} />}
            {viewState.isShowAddresList && <AddressSelect {...showAddressSelect} />}
            {viewState.isShowAddOrUpdateAddress && (
                <AddressAddUpdate
                    {...showAddOrUpdateAddress}
                    addUpdateAddress={addUpdateAddress}
                    validationError={validationError}
                    checkout={props.data.checkout.result}
                />
            )}
        </Module>
    );
};

export default CheckoutShippingAddressView;
